"use client";

import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  caseMeta,
  lifebenchData,
  type LifebenchCase,
  type LifebenchCaseMeta,
  type LifebenchEvent,
  type LifebenchTagType,
} from "./lifebenchData";

type StepIndex = 0 | 1 | 2 | 3;
type RevealStage = "A" | "B";
type PaneStatus = "center" | "enter-right" | "enter-left" | "exit-left" | "exit-right";

interface LeavingPaneSnapshot {
  id: number;
  flippedCardIds: ReadonlySet<string>;
  item: LifebenchCase;
  meta: LifebenchCaseMeta;
  revealRoute: boolean;
  stage: RevealStage;
  status: PaneStatus;
  step: StepIndex;
}

const STEP_INDEXES: StepIndex[] = [0, 1, 2, 3];

function getTypedClass(tagType: LifebenchTagType, prefix: string) {
  if (tagType === "world") return `${prefix}-world`;
  if (tagType === "user") return `${prefix}-user`;
  return `${prefix}-relation`;
}

function HighlightedText({ text }: { text: string }) {
  const parts = text.split(/(\*\*.*?\*\*)/g);

  return (
    <>
      {parts.map((part, index) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return (
            <span className="highlight" key={`${part}-${index}`}>
              {part.slice(2, -2)}
            </span>
          );
        }

        return <span key={`${part}-${index}`}>{part}</span>;
      })}
    </>
  );
}

function ArrowCircle() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function Header({
  activeCaseId,
  cases,
  onCaseSelect,
}: {
  activeCaseId: string;
  cases: readonly LifebenchCase[];
  onCaseSelect: (caseId: string) => void;
}) {
  return (
    <header className="app-header">
      <div className="header-row">
        <div className="case-list" aria-label="LivingBench cases">
          {cases.map((item) => (
            <button
              className={`case-btn${item.id === activeCaseId ? " active" : ""}`}
              type="button"
              key={item.id}
              onClick={() => onCaseSelect(item.id)}
            >
              {item.title}
            </button>
          ))}
        </div>
      </div>
    </header>
  );
}

function Footer({
  activeStep,
  onNext,
  onStepSelect,
}: {
  activeStep: StepIndex;
  onNext: () => void;
  onStepSelect: (step: StepIndex) => void;
}) {
  const prevDisabled = activeStep === 0;

  return (
    <footer className="app-footer">
      <div className="footer-row">
        <button
          className={`footer-btn footer-prev${prevDisabled ? " disabled" : ""}`}
          type="button"
          disabled={prevDisabled}
          onClick={() => onStepSelect((activeStep - 1) as StepIndex)}
        >
          <span className="footer-text">Prev</span>
        </button>
        <div className="step-dots">
          {STEP_INDEXES.map((step) => {
            const status = step === activeStep ? " active" : step < activeStep ? " done" : "";
            return (
              <button
                className={`step-dot${status}`}
                type="button"
                aria-label={`Go to step ${step + 1}`}
                key={step}
                onClick={() => onStepSelect(step)}
              />
            );
          })}
        </div>
        <button className="footer-btn footer-next" type="button" onClick={onNext}>
          <span className="footer-text">Next</span>
        </button>
      </div>
    </footer>
  );
}

function StepOne({
  item,
  meta,
  stage,
  onBack,
  onReveal,
}: {
  item: LifebenchCase;
  meta: LifebenchCaseMeta;
  stage: RevealStage;
  onBack: () => void;
  onReveal: () => void;
}) {
  return (
    <div className="step-wrap step1-wrap lang-en">
      <div className="stage-a">
        <section className="stage-hero" aria-label="Person entry">
          <p className="stage-eyebrow">LivingBench Case</p>
          <h1 className="stage-headline">
            <span className="headline-meet">Meet</span> <em>{meta.name}</em>
          </h1>
        </section>
        <article className="stage-entry-panel" data-flipped={stage === "B"}>
          <div className="stage-card-flip">
            <div className="stage-card-face stage-card-front">
              <div className="stage-front-media">
                <Image
                  className="stage-front-portrait"
                  src={meta.portrait}
                  alt={meta.name}
                  width={800}
                  height={800}
                  priority
                />
              </div>
              <div className="stage-panel-content">
                <div className="entry-detail">
                  <div className="stage-mark">
                    <span className="stage-mark-name">{meta.name}</span>
                    <span className="stage-mark-role">{meta.role}</span>
                  </div>
                  <p className="stage-subtitle">{item.title}</p>
                  <div className="stage-front-tags">
                    {item.step1.tags.map((tag) => (
                      <span className="stage-front-tag" key={tag}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <button
                  className="reveal-btn"
                  type="button"
                  aria-label="Show full profile"
                  onClick={onReveal}
                >
                  More about them
                </button>
              </div>
            </div>
            <div className="stage-card-face stage-card-back">
              <div className="stage-back-header">
                <span className="stage-eyebrow">Full profile</span>
                <button className="stage-back-btn" type="button" onClick={onBack}>
                  Back
                </button>
              </div>
              <h2>{meta.name}</h2>
              <div className="stage-back-copy">
                {item.step1.paragraphs.map((paragraph) => (
                  <p key={paragraph}>
                    <HighlightedText text={paragraph} />
                  </p>
                ))}
              </div>
              <div className="ai-message stage-back-message">
                <p>
                  <HighlightedText text={item.step1.aiMessage} />
                </p>
              </div>
              <div className="hook stage-back-hook">
                <HighlightedText text={item.step1.hook} />
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}

function StepTwo({
  flippedCardIds,
  item,
  onToggleCard,
  revealRoute,
}: {
  flippedCardIds: ReadonlySet<string>;
  item: LifebenchCase;
  onToggleCard: (cardId: string) => void;
  revealRoute: boolean;
}) {
  return (
    <div className="step-wrap step2-wrap lang-en">
      <div className="step-heading">
        <p className="step-intro">{item.step2.introLine}</p>
      </div>
      <div className="cards-grid">
        {item.step2.cards.map((card, index) => {
          const optionText = `Option ${index + 1}`;
          const frontLabel = `${optionText}: ${card.name}, ${card.keyNumber}`;
          const backLabel = `${optionText} Constraint: ${card.fatalFlaw}`;
          const flipped = flippedCardIds.has(card.id);

          return (
            <button
              className="flip-card"
              type="button"
              aria-label={flipped ? backLabel : frontLabel}
              aria-pressed={flipped}
              style={{ animationDelay: `${0.16 + index * 0.08}s` }}
              key={card.id}
              onClick={() => onToggleCard(card.id)}
            >
              <span className={`flip-card-inner${flipped ? " flipped" : ""}`}>
                <span className="card-face card-front">
                  <span className="card-topline">
                    <span className="card-kicker">{optionText}</span>
                    <span className="card-hint">Flip to see the catch</span>
                  </span>
                  <span className="card-body">
                    <span className="card-title">{card.name}</span>
                    <span className="card-number">{card.keyNumber}</span>
                  </span>
                </span>
                <span className="card-face card-back">
                  <span className="card-back-header">
                    <span className="constraint-label">Constraint</span>
                  </span>
                  <span className="card-flaw">{card.fatalFlaw}</span>
                </span>
              </span>
            </button>
          );
        })}
      </div>
      <div className="post-flip">
        <div className={`hidden-route-wrap${revealRoute ? " revealed" : ""}`}>
          <div className="hidden-route-box" data-route-label="The narrow route">
            <p>{item.step2.hiddenRoute}</p>
          </div>
        </div>
        <div className={`post-flip-outro${revealRoute ? " revealed" : ""}`}>{item.step2.outroLine}</div>
      </div>
    </div>
  );
}

function StepThree({ item }: { item: LifebenchCase }) {
  return (
    <div className="step-wrap step3-wrap lang-en">
      <div className="step-heading">
        <p className="step-intro">{item.step3.introLine}</p>
      </div>
      <div className="events">
        {item.step3.events.map((event: LifebenchEvent, index) => (
          <div
            className={`event-row ${getTypedClass(event.tagType, "event")}`}
            style={{ animationDelay: `${0.4 + index * 0.3}s` }}
            key={`${event.tag}-${event.description}`}
          >
            <span className={`event-tag ${getTypedClass(event.tagType, "tag")}`}>{event.tag}</span>
            <div className="event-copy">
              <span className="event-description">{event.description}</span>
              <div className={`event-result ${getTypedClass(event.tagType, "result")}`}>→ {event.result}</div>
            </div>
          </div>
        ))}
      </div>
      <div
        className="step-outro"
        style={{ animationDelay: `${0.6 + item.step3.events.length * 0.3 + 0.4}s` }}
      >
        {item.step3.outroLine}
      </div>
    </div>
  );
}

function StepFour({ item }: { item: LifebenchCase }) {
  return (
    <div className="step-wrap step4-wrap lang-en">
      <div className="step-heading">
        <p className="step-intro">{item.step4.introLine}</p>
      </div>
      <div className="evaluations">
        {item.step4.evaluations.map((evaluation, index) => (
          <div
            className="evaluation-row"
            style={{ animationDelay: `${0.3 + index * 0.25}s` }}
            key={`${evaluation.setting}-${evaluation.requirement}`}
          >
            <div className="eval-setting">{evaluation.setting}</div>
            <div className="desktop-arrow">
              <ArrowCircle />
            </div>
            <div className="eval-requirement">
              <span className="mobile-arrow">→</span>
              {evaluation.requirement}
            </div>
          </div>
        ))}
      </div>
      <div
        className="step-outro"
        style={{ animationDelay: `${0.5 + item.step4.evaluations.length * 0.25 + 0.4}s` }}
      >
        {item.step4.outroLine}
      </div>
    </div>
  );
}

function CurrentStep({
  activeStep,
  flippedCardIds,
  item,
  meta,
  onBack,
  onReveal,
  onToggleCard,
  revealRoute,
  stage,
}: {
  activeStep: StepIndex;
  flippedCardIds: ReadonlySet<string>;
  item: LifebenchCase;
  meta: LifebenchCaseMeta;
  onBack: () => void;
  onReveal: () => void;
  onToggleCard: (cardId: string) => void;
  revealRoute: boolean;
  stage: RevealStage;
}) {
  if (activeStep === 0) {
    return <StepOne item={item} meta={meta} stage={stage} onBack={onBack} onReveal={onReveal} />;
  }

  if (activeStep === 1) {
    return (
      <StepTwo
        flippedCardIds={flippedCardIds}
        item={item}
        onToggleCard={onToggleCard}
        revealRoute={revealRoute}
      />
    );
  }

  if (activeStep === 2) {
    return <StepThree item={item} />;
  }

  return <StepFour item={item} />;
}

export default function LifebenchDemo() {
  const stepPaneRef = useRef<HTMLDivElement>(null);
  const transitionTimersRef = useRef<number[]>([]);
  const [activeCaseId, setActiveCaseId] = useState<string>(lifebenchData.en[0].id);
  const [activeStep, setActiveStep] = useState<StepIndex>(0);
  const [stage, setStage] = useState<RevealStage>("A");
  const [flippedCardIds, setFlippedCardIds] = useState<Set<string>>(() => new Set());
  const [revealRoute, setRevealRoute] = useState(false);
  const [transitionId, setTransitionId] = useState(0);
  const [currentPaneStatus, setCurrentPaneStatus] = useState<PaneStatus>("center");
  const [leavingPane, setLeavingPane] = useState<LeavingPaneSnapshot | null>(null);

  const cases = lifebenchData.en;
  const activeCase = useMemo(
    () => cases.find((item) => item.id === activeCaseId) ?? cases[0],
    [activeCaseId, cases],
  );
  const activeMeta = caseMeta[activeCase.id] ?? caseMeta["1"];

  useEffect(() => {
    if (activeStep !== 1) {
      setFlippedCardIds(new Set());
      setRevealRoute(false);
      return;
    }

    setFlippedCardIds(new Set());
    setRevealRoute(false);
    const flipTimer = window.setTimeout(() => {
      setFlippedCardIds(new Set(activeCase.step2.cards.map((card) => card.id)));
    }, 3000);
    const revealTimer = window.setTimeout(() => {
      setRevealRoute(true);
    }, 4300);

    return () => {
      window.clearTimeout(flipTimer);
      window.clearTimeout(revealTimer);
    };
  }, [activeCase, activeStep]);

  useEffect(() => {
    return () => {
      transitionTimersRef.current.forEach((timer) => window.clearTimeout(timer));
      transitionTimersRef.current = [];
    };
  }, []);

  function scrollViewportToTop() {
    document
      .querySelectorAll<HTMLDivElement>(".step-pane")
      .forEach((pane) => {
        pane.scrollTop = 0;
      });

    if (stepPaneRef.current) {
      stepPaneRef.current.scrollTop = 0;
    }
  }

  function clearTransitionTimers() {
    transitionTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    transitionTimersRef.current = [];
  }

  function startPaneTransition(step: StepIndex, direction: -1 | 0 | 1, caseId = activeCaseId) {
    scrollViewportToTop();
    clearTransitionTimers();

    if (direction === 0) {
      setLeavingPane(null);
      setCurrentPaneStatus("center");
    } else {
      setLeavingPane({
        id: transitionId,
        flippedCardIds: new Set(flippedCardIds),
        item: activeCase,
        meta: activeMeta,
        revealRoute,
        stage,
        status: direction > 0 ? "exit-left" : "exit-right",
        step: activeStep,
      });
      setCurrentPaneStatus(direction > 0 ? "enter-right" : "enter-left");

      window.requestAnimationFrame(() => {
        setCurrentPaneStatus("center");
      });

      const cleanupTimer = window.setTimeout(() => {
        setLeavingPane(null);
      }, 340);
      transitionTimersRef.current.push(cleanupTimer);
    }

    setTransitionId((id) => id + 1);
    setActiveCaseId(caseId);
    setActiveStep(step);
    setStage("A");
  }

  function resetToStep(step: StepIndex) {
    const direction = step > activeStep ? 1 : step < activeStep ? -1 : 0;
    startPaneTransition(step, direction);
  }

  function handleCaseSelect(caseId: string) {
    if (caseId === activeCaseId && activeStep === 0) {
      return;
    }

    startPaneTransition(0, 0, caseId);
  }

  function goToNextCase() {
    const currentIndex = cases.findIndex((item) => item.id === activeCase.id);
    const nextCase = cases[(currentIndex + 1 + cases.length) % cases.length];
    startPaneTransition(0, 1, nextCase.id);
  }

  function handleNextStep() {
    if (activeStep === 3) {
      goToNextCase();
      return;
    }

    startPaneTransition((activeStep + 1) as StepIndex, 1);
  }

  function handleToggleCard(cardId: string) {
    setFlippedCardIds((previous) => {
      const next = new Set(previous);
      if (next.has(cardId)) {
        next.delete(cardId);
      } else {
        next.add(cardId);
      }
      return next;
    });
  }

  return (
    <div className="lifebench-root" lang="en">
      <div className="app-shell">
        <Header
          activeCaseId={activeCase.id}
          cases={cases}
          onCaseSelect={handleCaseSelect}
        />
        <main className="app-main">
          <div className="viewport">
            {leavingPane ? (
              <div className={`step-pane ${leavingPane.status}`} key={`leaving-${leavingPane.id}`}>
                <CurrentStep
                  activeStep={leavingPane.step}
                  flippedCardIds={leavingPane.flippedCardIds}
                  item={leavingPane.item}
                  meta={leavingPane.meta}
                  onBack={() => undefined}
                  onReveal={() => undefined}
                  onToggleCard={() => undefined}
                  revealRoute={leavingPane.revealRoute}
                  stage={leavingPane.stage}
                />
              </div>
            ) : null}
            <div className={`step-pane current ${currentPaneStatus}`} key={`current-${transitionId}`} ref={stepPaneRef}>
              <CurrentStep
                activeStep={activeStep}
                flippedCardIds={flippedCardIds}
                item={activeCase}
                meta={activeMeta}
                onBack={() => setStage("A")}
                onReveal={() => setStage("B")}
                onToggleCard={handleToggleCard}
                revealRoute={revealRoute}
                stage={stage}
              />
            </div>
          </div>
        </main>
        <Footer activeStep={activeStep} onNext={handleNextStep} onStepSelect={resetToStep} />
      </div>
    </div>
  );
}
